import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../../utils/api';
import { fetchWithAuth } from '../../services/api';

interface UserDetailPageProps {
  userId?: string;
  onNavigate: (page: string, data?: any) => void;
}

interface User {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  department: string;
  position: string;
  roles: string;
  active: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

interface Role {
  roleId: number;
  roleCode: string;
  roleName: string;
  description?: string;
  isActive: boolean;
}

interface FormData {
  username: string;
  fullName: string;
  email: string;
  department: string;
  position: string;
  roles: string[];
  active: boolean;
  version?: number;
}

interface ApprovalRouteUsage {
  count: number;
  applicationTypes: string[];
}

export function UserDetailPage({ userId, onNavigate }: UserDetailPageProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState<FormData>({
    username: '',
    fullName: '',
    email: '',
    department: '',
    position: '',
    roles: [],
    active: true
  });

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [approvalUsage, setApprovalUsage] = useState<ApprovalRouteUsage | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  // Update confirmation states
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  // Create confirmation states
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);

  // Refs for validation
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const fullNameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const isNewUser = !userId;

  useEffect(() => {
    fetchRoles();
    if (userId) {
      loadUser();
    }
  }, [userId]);

  const fetchRoles = async () => {
    try {
      const response = await fetchWithAuth(getApiUrl('/admin/roles'));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableRoles(data.data);
        }
      }
    } catch (error) {
      console.error('ロール取得エラー:', error);
    }
  };

  const loadUser = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(getApiUrl(`/users/${userId}`));

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const userData = result.data;
          setUser(userData);
          setFormData({
            username: userData.username,
            fullName: userData.fullName,
            email: userData.email,
            department: userData.department,
            position: userData.position,
            roles: userData.roles ? userData.roles.split(',').map((r: string) => r.trim()) : [],
            active: userData.active,
            version: userData.version
          });
        } else {
          setError(result.message || 'ユーザーの取得に失敗しました');
        }
      } else {
        setError('ユーザーの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('ユーザーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | string[] | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (roleCode: string) => {
    const newRoles = formData.roles.includes(roleCode)
      ? formData.roles.filter(r => r !== roleCode)
      : [...formData.roles, roleCode];
    setFormData({ ...formData, roles: newRoles });
  };

  const validateForm = (): boolean => {
    if (!formData.username) {
      if (usernameInputRef.current) {
        usernameInputRef.current.setCustomValidity('ユーザーIDを入力してください');
        usernameInputRef.current.reportValidity();
      }
      return false;
    }

    if (!formData.fullName) {
      if (fullNameInputRef.current) {
        fullNameInputRef.current.setCustomValidity('氏名を入力してください');
        fullNameInputRef.current.reportValidity();
      }
      return false;
    }

    if (!formData.email) {
      if (emailInputRef.current) {
        emailInputRef.current.setCustomValidity('メールアドレスを入力してください');
        emailInputRef.current.reportValidity();
      }
      return false;
    }

    return true;
  };

  const handleSaveButtonClick = () => {
    if (!validateForm()) {
      return;
    }

    if (isNewUser) {
      setShowCreateConfirm(true);
    } else {
      setShowUpdateConfirm(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const url = isNewUser
        ? getApiUrl('/users')
        : getApiUrl(`/users/${userId}`);

      const method = isNewUser ? 'POST' : 'PUT';
      const bodyData = {
        ...formData,
        roles: formData.roles.join(',')
      };

      if (isNewUser) {
        delete bodyData.version;
      }

      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setShowCreateConfirm(false);
          setShowUpdateConfirm(false);
          onNavigate('users');
        } else {
          setError(`保存に失敗しました: ${result.message}`);
        }
      } else {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
        setError(`保存に失敗しました: ${errorMessage}`);
      }
    } catch (error) {
      console.error('保存エラー:', error);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const checkApprovalRouteUsage = async (userId: string): Promise<ApprovalRouteUsage> => {
    try {
      const response = await fetchWithAuth(getApiUrl(`/approval-routes/usage/${userId}`));
      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : { count: 0, applicationTypes: [] };
      }
    } catch (error) {
      console.error('承認ルート使用状況取得エラー:', error);
    }
    return { count: 0, applicationTypes: [] };
  };

  const handleDeleteButtonClick = async () => {
    if (!user) return;

    try {
      setDeleteLoading(true);
      const usage = await checkApprovalRouteUsage(user.userId);
      setApprovalUsage(usage);

      if (usage.count > 0) {
        setShowDeleteWarning(true);
      } else {
        setShowDeleteConfirm(true);
      }
    } catch (error) {
      console.error('削除処理エラー:', error);
      setError('削除処理に失敗しました');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!user) return;

    try {
      setDeleteLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(`/users/${user.userId}`),
        { method: 'DELETE' }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          onNavigate('users');
        } else {
          setError(`削除に失敗しました: ${result.message}`);
          setShowDeleteConfirm(false);
        }
      } else {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
        setError(`削除に失敗しました: ${errorMessage}`);
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      console.error('削除エラー:', error);
      setError('削除に失敗しました');
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(`/users/${user.userId}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            active: !formData.active,
            roles: formData.roles.join(',')
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setShowDeleteWarning(false);
          await loadUser();
        } else {
          setError(`ステータス更新に失敗しました: ${result.message}`);
        }
      } else {
        setError('ステータス更新に失敗しました');
      }
    } catch (error) {
      console.error('ステータス更新エラー:', error);
      setError('ステータス更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const formatRoles = (roles: string[]) => {
    if (!roles || roles.length === 0) return '';
    const roleLabels = roles.map(role => {
      const option = availableRoles.find(r => r.roleCode === role);
      return option ? option.roleName : role;
    });
    return roleLabels.join(', ');
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="px-8 py-6">
      <div className="page-header">
        <button
          onClick={() => onNavigate('users')}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          style={{ marginBottom: '1.5rem' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          一覧に戻る
        </button>
        <div style={{
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.75rem',
          marginBottom: '2rem'
        }}>
          <h1 className="page-title">
            {isNewUser ? '新規ユーザー作成' : 'ユーザー詳細'}
          </h1>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div className="flex gap-4">
          <button
            onClick={handleSaveButtonClick}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: '1px solid #2563eb',
              borderRadius: '0.375rem',
              color: 'white',
              backgroundColor: '#2563eb',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }}
          >
            {saving ? '保存中...' : '保存'}
          </button>
          {!isNewUser && user && (
            <button
              onClick={handleDeleteButtonClick}
              disabled={deleteLoading}
              className="btn"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #dc2626',
                borderRadius: '0.375rem',
                color: '#dc2626',
                backgroundColor: 'white',
                cursor: deleteLoading ? 'not-allowed' : 'pointer',
                opacity: deleteLoading ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!deleteLoading) {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {deleteLoading ? '確認中...' : '削除'}
            </button>
          )}
        </div>
      </div>

      {/* 基本情報 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.3rem' }}>
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
            {!isNewUser && user && (
              <span style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '0.375rem',
                backgroundColor: formData.active ? '#dcfce7' : '#f3f4f6',
                color: formData.active ? '#166534' : '#374151'
              }}>
                {formData.active ? '有効' : '無効'}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-6">
          {/* ユーザーID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ユーザーID <span className="text-red-600">*</span>
            </label>
            <input
              ref={usernameInputRef}
              type="text"
              value={formData.username}
              onChange={(e) => {
                handleChange('username', e.target.value);
                if (usernameInputRef.current) {
                  usernameInputRef.current.setCustomValidity('');
                }
              }}
              disabled={!isNewUser}
              placeholder="user001"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isNewUser ? 'ログイン時に使用するID' : '※IDは変更できません'}
            </p>
          </div>

          {/* 氏名 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              氏名 <span className="text-red-600">*</span>
            </label>
            <input
              ref={fullNameInputRef}
              type="text"
              value={formData.fullName}
              onChange={(e) => {
                handleChange('fullName', e.target.value);
                if (fullNameInputRef.current) {
                  fullNameInputRef.current.setCustomValidity('');
                }
              }}
              placeholder="山田 太郎"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* メールアドレス */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              メールアドレス <span className="text-red-600">*</span>
            </label>
            <input
              ref={emailInputRef}
              type="email"
              value={formData.email}
              onChange={(e) => {
                handleChange('email', e.target.value);
                if (emailInputRef.current) {
                  emailInputRef.current.setCustomValidity('');
                }
              }}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 部署 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              部署
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              placeholder="総務部"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 役職 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              役職
            </label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => handleChange('position', e.target.value)}
              placeholder="スタッフ"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* ロール */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ロール
            </label>
            <div className="space-y-2">
              {availableRoles.length > 0 ? (
                availableRoles.map(role => (
                  <label key={role.roleCode} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role.roleCode)}
                      onChange={() => handleRoleToggle(role.roleCode)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm">{role.roleName}</span>
                  </label>
                ))
              ) : (
                <div className="text-red-600 text-sm">
                  ロール一覧を取得できませんでした
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              選択: {formatRoles(formData.roles) || 'なし'}
            </p>
          </div>

          {/* ステータス */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ステータス
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">有効</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              無効にするとログインできなくなります
            </p>
          </div>
        </div>
      </div>

      {/* Create Confirmation Dialog */}
      {showCreateConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">作成確認</h2>
            <p className="text-gray-700 mb-2">
              以下のユーザーを作成してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>ユーザーID:</strong> {formData.username}</div>
                <div><strong>氏名:</strong> {formData.fullName}</div>
                <div><strong>メール:</strong> {formData.email}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateConfirm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '作成中...' : '作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Dialog */}
      {showUpdateConfirm && user && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">更新確認</h2>
            <p className="text-gray-700 mb-2">
              以下のユーザーを更新してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>ユーザーID:</strong> {user.username}</div>
                <div><strong>氏名:</strong> {formData.fullName}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUpdateConfirm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '更新中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && user && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">削除確認</h2>
            <p className="text-gray-700 mb-2">
              以下のユーザーを削除してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>ユーザーID:</strong> {user.username}</div>
                <div><strong>氏名:</strong> {user.fullName}</div>
              </div>
            </div>
            <p className="text-red-600 text-sm mt-4">
              ※ この操作は元に戻せません。
            </p>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
                disabled={deleteLoading}
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                disabled={deleteLoading}
              >
                {deleteLoading ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 承認ルート使用警告モーダル */}
      {showDeleteWarning && approvalUsage && user && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-yellow-800">削除できません</h3>
            </div>

            <p className="text-gray-700 mb-4">
              <strong>{user.fullName}</strong> さんは以下の承認ルートで承認者として設定されているため、削除できません。
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
              <h4 className="font-medium text-yellow-800 mb-2">使用中の承認ルート:</h4>
              <ul className="list-disc list-inside text-sm text-yellow-700">
                {approvalUsage.applicationTypes.map((type, index) => (
                  <li key={index}>{type}</li>
                ))}
              </ul>
              <p className="text-sm text-yellow-700 mt-2">
                合計 <strong>{approvalUsage.count}</strong> 件の承認ルートで使用中です。
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-4">
              <h4 className="font-medium text-blue-800 mb-1">対応方法:</h4>
              <p className="text-sm text-blue-700">
                1. 承認ルート設定で代替承認者を設定してください<br/>
                2. または、ユーザーを無効化してください
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteWarning(false)}
                className="btn btn-secondary"
              >
                閉じる
              </button>
              <button
                onClick={handleToggleActive}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '無効化中...' : '代わりに無効化する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
